import { Currency, Money } from "@hubrise/react-components"

import { DiscountId } from "shared/hubrise/ids"
import { DiscountPricing, ICatalog, IDiscount } from "shared/hubrise/models"
import {
  findById,
  insertItemInArray,
  moveItemWithinArray,
  removeInArrayById,
  replaceInArrayById,
} from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"

/**
 * Add a discount.
 */
export function add(
  catalog: ICatalog,
  insertAfter: IDiscount | undefined,
  values: Pick<IDiscount, "name">,
): { newCatalog: ICatalog; discount: IDiscount } {
  const position: number = insertAfter
    ? catalog.data.discounts.findIndex((discount) => discount.id === insertAfter.id) + 1
    : 0
  const id = DiscountId(catalog.idGenerator())
  const defaultValues: IDiscount = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id,
    ref: "",
    name: "",
    description: "",
    restrictions: { enabled: true },
    couponCodes: [],
    pricingEffect: "percentage_off",
    pricingValue: 0,
    imageHrIds: [],
  }
  const discount: IDiscount = { ...defaultValues, ...values }
  const discounts: Array<IDiscount> = insertItemInArray(catalog.data.discounts, discount, position)
  const newCatalog: ICatalog = updatedCatalog(catalog, { discounts })
  return { newCatalog, discount }
}

/**
 * Delete the discount. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param id
 */
export function doDelete(catalog: ICatalog, id: DiscountId): ICatalog {
  return updatedCatalog(catalog, { discounts: removeInArrayById(catalog.data.discounts, id) })
}

/**
 * Return the discount matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: DiscountId): IDiscount => {
  const discount = findById(catalog.data.discounts, id)
  if (!discount) throw `Discount ${id} not found`
  return discount
}

/**
 * Move a discount to a new position.
 * @param catalog
 * @param discount
 * @param newPosition
 */
export function move(catalog: ICatalog, discount: IDiscount, newPosition: number): ICatalog {
  const discounts: Array<IDiscount> = moveItemWithinArray(catalog.data.discounts, discount, newPosition)
  return updatedCatalog(catalog, { discounts })
}

/**
 * Return the new pricing of a discount, after switching its pricing effect.
 * When the type of the pricing value changes, the pricing value we return is calculated from the older value.
 * @param dealLine
 * @param pricingEffect
 * @param currency
 */
export function switchPricingEffect(
  dealLine: IDiscount,
  pricingEffect: DiscountPricing["pricingEffect"],
  currency: Currency,
): DiscountPricing {
  const asMoney = (pricing: DiscountPricing): Money => {
    switch (pricing.pricingEffect) {
      case "price_off":
        return pricing.pricingValue
      case "percentage_off":
        return new Money(Math.round(pricing.pricingValue * 100), currency)
    }
  }

  const asPercentage = (pricing: DiscountPricing): number => {
    switch (pricing.pricingEffect) {
      case "price_off":
        return pricing.pricingValue.cents / 100
      case "percentage_off":
        return pricing.pricingValue
    }
  }

  switch (pricingEffect) {
    case "price_off":
      return { pricingEffect, pricingValue: asMoney(dealLine) }
    case "percentage_off":
      return { pricingEffect, pricingValue: asPercentage(dealLine) }
  }
}

/**
 * Update a discount.
 * @param catalog
 * @param id
 * @param delta
 */
export function update<T extends keyof IDiscount>(
  catalog: ICatalog,
  id: DiscountId,
  fieldName: T,
  value: IDiscount[T],
): ICatalog {
  return updatedCatalog(catalog, {
    discounts: replaceInArrayById(catalog.data.discounts, id, (discount) => ({
      ...discount,
      [fieldName]: value,
    })),
  })
}

/**
 * Update the pricing of a discount.
 * @param catalog
 * @param id
 * @param pricing
 */
export function updatePricing(catalog: ICatalog, id: DiscountId, pricing: DiscountPricing): ICatalog {
  return updatedCatalog(catalog, {
    discounts: replaceInArrayById(catalog.data.discounts, id, (discount) => ({
      ...discount,
      ...pricing,
    })),
  })
}
